import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, Users, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const gbp = (n) => `${n < 0 ? "−" : ""}£${Math.abs(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };

export function LandlordImportCard({ onImported }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (dry) => {
    setBusy(true);
    try {
      const r = await api.post("/landlords/import", { text, dry_run: dry });
      setPreview(r.data);
      if (!dry) { toast.success(`${r.data.matched.length} properties updated`); onImported?.(); }
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <section className="ns-card p-6" data-testid="landlord-import-card">
      <div className="font-serif text-lg flex items-center gap-2 mb-1"><Users className="w-4 h-4" /> Import landlords</div>
      <p className="text-xs text-slate-500 mb-3">One per line: <span className="font-mono">property name, landlord name, email</span>. Commas or tabs (paste from a spreadsheet) both work.</p>
      <Textarea rows={5} className="bg-white font-mono text-xs" value={text} data-testid="landlord-import-text"
        placeholder={"Cutters House, Jane Smith, jane@example.com\nGlynn House, Tom Jones, tom@example.com"}
        onChange={(e) => { setText(e.target.value); setPreview(null); }} />
      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" className="rounded-full" disabled={!text.trim() || busy} onClick={() => run(true)} data-testid="landlord-import-preview">Preview</Button>
        <Button size="sm" className="rounded-full ns-btn-primary" disabled={!preview || !preview.matched.length || busy || !preview.dry_run} onClick={() => run(false)} data-testid="landlord-import-apply">
          Apply {preview?.dry_run ? preview.matched.length : ""}
        </Button>
      </div>
      {preview && (
        <div className="mt-3 text-xs space-y-1" data-testid="landlord-import-result">
          {preview.matched.map((m) => (
            <div key={m.line} className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="truncate">{m.property_name.replace(/\*/g, " · ")}</span><span className="text-slate-500">→ {m.owner_name} &lt;{m.owner_email}&gt;</span></div>
          ))}
          {preview.errors.map((e) => <div key={e.line} className="text-rose-700">Line {e.line}: {e.reason}</div>)}
        </div>
      )}
    </section>
  );
}

export function StatementQueueCard({ refreshKey }) {
  const [month, setMonth] = useState(lastMonth());
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/statements/queue?month=${month}`).then((r) => {
    setRows(r.data.rows);
    setSel(Object.fromEntries(r.data.rows.filter((x) => x.owner_email && !x.sent_at && (x.income || x.expenses)).map((x) => [x.property_id, true])));
  }).catch((err) => toast.error(apiError(err)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, refreshKey]);
  const chosen = Object.keys(sel).filter((k) => sel[k]);
  const preview = async (pid) => {
    const r = await api.get(`/statements/${pid}/pdf?month=${month}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const sendAll = async () => {
    if (!window.confirm(`Email ${chosen.length} landlord statement(s) for ${month}?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/statements/bulk-send", { month, property_ids: chosen, force: true });
      toast.success(`${r.data.sent} of ${chosen.length} sent`);
      r.data.results.filter((x) => !x.ok).forEach((x) => toast.error(x.error || "Send failed"));
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <section className="ns-card p-6" data-testid="statement-queue-card">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-lg flex items-center gap-2"><FileText className="w-4 h-4" /> Statement approval queue</div>
          <p className="text-xs text-slate-500">Review each landlord's profit statement, preview the PDF, then send the selected ones together.</p>
        </div>
        <div className="flex items-end gap-2">
          <Input type="month" className="h-9 bg-white w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="statement-queue-month" />
          <Button size="sm" className="rounded-full ns-btn-primary h-9" disabled={!chosen.length || busy} onClick={sendAll} data-testid="statement-queue-send">
            <Send className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Sending…" : `Send ${chosen.length} selected`}
          </Button>
        </div>
      </div>
      <table className="w-full text-xs" data-testid="statement-queue-table">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <th className="py-2 w-6"></th><th>Property</th><th>Landlord</th><th className="text-right">Income</th><th className="text-right">Costs</th><th className="text-right">Profit</th><th>Notes</th><th>Status</th><th></th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.property_id} className={`border-b border-slate-50 ${!r.income && !r.expenses ? "opacity-50" : ""}`} data-testid={`statement-queue-row-${r.property_id}`}>
              <td className="py-2"><input type="checkbox" disabled={!r.owner_email} checked={!!sel[r.property_id]} data-testid={`statement-queue-check-${r.property_id}`}
                onChange={(e) => setSel({ ...sel, [r.property_id]: e.target.checked })} /></td>
              <td className="max-w-[200px] truncate">{(r.property_name || "").replace(/\*/g, " · ")}</td>
              <td className="text-slate-600">{r.owner_email ? (r.owner_name || r.owner_email) : <span className="text-amber-700">No landlord email</span>}</td>
              <td className="text-right font-mono">{gbp(r.income)}</td>
              <td className="text-right font-mono">{gbp(r.expenses)}</td>
              <td className={`text-right font-mono ${r.net < 0 ? "text-rose-700" : "text-emerald-700"}`}>{gbp(r.net)}</td>
              <td className="text-slate-500 max-w-[140px] truncate" title={r.notes}>{r.notes || "—"}</td>
              <td>{r.sent_at ? <span className="text-emerald-700">Sent {r.sent_at.slice(0, 10)}</span> : <span className="text-slate-400">Draft</span>}</td>
              <td><button onClick={() => preview(r.property_id)} className="text-teal-700 hover:underline" data-testid={`statement-queue-preview-${r.property_id}`}>Preview</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-400 mt-2">Add notes per property from Properties → edit → Landlord profit statement.</p>
    </section>
  );
}
